import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Megaphone, Info, Loader2, Bot, Search } from 'lucide-react';
import { Card } from '../ui/Card';
import { Sparkline } from './tmCharts';
import { Switch } from './SetupSteps';
import { useFmt, useLabels } from './tmShared';
import trafficManagerService, { EligibleCampaign, Evolution, TrafficManager } from '../../services/trafficManagerService';

type Periodo = 'ultimos_7_dias' | 'ultimos_30_dias' | 'no_mes';
type Filtro = 'managed' | 'out' | 'all';

/**
 * Campanhas: quais o robô gere e quais ficam de fora, com os números de cada.
 * - Todas as publicadas (padrão): entra tudo, menos as que você excluir.
 * - Só as escolhidas: entra só o que estiver marcado.
 * Campanhas novas entram sozinhas se ligado; as que o próprio robô cria
 * (remarketing) entram sempre, a menos que você exclua.
 */
const CampaignsTab: React.FC<{
  manager: TrafficManager;
  evolution: Evolution | null;
  eligible: EligibleCampaign[];
  onSeeChanges: (campaignId: string) => void;
  onScopeSaved: () => Promise<void> | void;
}> = ({ manager, evolution, eligible, onSeeChanges, onScopeSaved }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const L = useLabels();
  const cur = manager.currency || 'BRL';
  const [periodo, setPeriodo] = useState<Periodo>('ultimos_30_dias');
  const [filtro, setFiltro] = useState<Filtro>('managed');
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState<string | null>(null);
  const [verParadas, setVerParadas] = useState(false);

  const ids = manager.campaign_ids || [];
  const g = manager.guardrails_effective || manager.guardrails || ({} as any);
  const excluidas: string[] = g.excluded_campaign_ids || [];
  const incluiNovas = g.include_new_campaigns !== false;
  const modo: 'all' | 'selected' = ids.length ? 'selected' : 'all';
  const snap = useMemo(() => Object.fromEntries((manager.snapshot?.campanhas || []).map((c) => [c.id, c])), [manager.snapshot]);

  const salvar = async (chave: string, patch: { campaign_ids?: string[]; excluded?: string[]; include_new?: boolean }) => {
    setSalvando(chave);
    try {
      await trafficManagerService.update(manager.id, {
        ...(patch.campaign_ids ? { campaign_ids: patch.campaign_ids } : {}),
        guardrails: {
          ...(patch.excluded ? { excluded_campaign_ids: patch.excluded } : {}),
          ...(patch.include_new !== undefined ? { include_new_campaigns: patch.include_new } : {})
        }
      });
      await onScopeSaved();
      toast.success(t('trafficManager.scope.saved', 'Salvo. Vale a partir da próxima rodada.'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
    } finally { setSalvando(null); }
  };

  const alternar = (c: EligibleCampaign) => {
    const gerir = !c.managed;
    if (modo === 'all') {
      salvar(c.id, { excluded: gerir ? excluidas.filter((x) => x !== c.id) : [...excluidas, c.id] });
      return;
    }
    if (!gerir && ids.length === 1 && ids[0] === c.id) {
      toast.error(t('trafficManager.scope.keepOne', 'Deixe pelo menos uma marcada, ou volte para "Todas as publicadas".'));
      return;
    }
    salvar(c.id, gerir
      ? { campaign_ids: [...new Set([...ids, c.id])], excluded: excluidas.filter((x) => x !== c.id) }
      : { campaign_ids: ids.filter((x) => x !== c.id), excluded: [...new Set([...excluidas, c.id])] });
  };

  const mudarModo = (novo: 'all' | 'selected') => {
    if (novo === modo) return;
    // Ao passar para "escolhidas", começa com o que já está sob gestão: nada muda até você desmarcar.
    if (novo === 'selected') {
      const atuais = eligible.filter((c) => c.managed).map((c) => c.id);
      if (!atuais.length) { toast.error(t('trafficManager.scope.noneToPick', 'Nenhuma campanha sob gestão para começar a lista.')); return; }
      salvar('mode', { campaign_ids: atuais });
    } else {
      salvar('mode', { campaign_ids: [] });
    }
  };

  const rodando = (c: EligibleCampaign) => {
    const s = snap[c.id];
    if (s && s.rodando_na_plataforma != null) return s.rodando_na_plataforma;
    return c.running != null ? c.running : c.status === 'active';
  };
  const numeros = (c: EligibleCampaign) => {
    const s = snap[c.id];
    if (s) return { ...s[periodo], aprox: false };
    // Fora da gestão (ou ainda sem rodada): só os 30 dias da lista.
    return { spend: c.spend_30d, conversions: c.conversions_30d, cpa: c.cpa_30d, cpc: null, ctr: null, aprox: periodo !== 'ultimos_30_dias' };
  };

  const geridas = eligible.filter((c) => c.managed);
  const fora = eligible.filter((c) => !c.managed);
  const q = busca.trim().toLowerCase();
  const base = (filtro === 'managed' ? geridas : filtro === 'out' ? fora : eligible).filter((c) => !q || c.name.toLowerCase().includes(q));
  const ordenadas = [...base].sort((a, b) => (Number(rodando(b)) - Number(rodando(a))) || (numeros(b).spend - numeros(a).spend));
  const comAtividade = ordenadas.filter((c) => rodando(c) || numeros(c).spend > 0);
  const paradas = ordenadas.length - comAtividade.length;
  const visiveis = verParadas || q ? ordenadas : comAtividade;

  const ultimaMudanca = (id: string) => {
    const lista = (evolution?.acoes || []).filter((a) => a.campaign_id === id);
    return lista.length ? lista[lista.length - 1] : null;
  };
  const seg = (on: boolean) => `px-3 py-1.5 text-sm rounded-md transition-colors ${on ? 'bg-white dark:bg-gray-900 shadow-sm text-gray-900 dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'}`;

  if (!eligible.length) {
    return <Card><p className="text-sm text-gray-600 dark:text-gray-300 flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" />{t('trafficManager.noCampaigns', 'Nenhuma campanha publicada dentro do escopo do gestor. Publique uma campanha no Google, Meta ou LinkedIn para ele começar.')}</p></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-gray-900 dark:text-white">{t('trafficManager.scope.title', 'O que o robô pode mexer')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('trafficManager.scope.help', 'Ligue ou desligue cada campanha na lista. Campanha fora da gestão nunca é tocada: o robô só lê os números dela para comparar.')}</p>
            <div className="mt-3 inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1" role="radiogroup">
              <button type="button" role="radio" aria-checked={modo === 'all'} disabled={!!salvando} onClick={() => mudarModo('all')} className={seg(modo === 'all')}>{t('trafficManager.scope.all', 'Todas as publicadas')}</button>
              <button type="button" role="radio" aria-checked={modo === 'selected'} disabled={!!salvando} onClick={() => mudarModo('selected')} className={seg(modo === 'selected')}>{t('trafficManager.scope.selected', 'Só as que eu escolher')}</button>
              {salvando === 'mode' && <Loader2 className="w-4 h-4 animate-spin self-center mx-2 text-gray-400" />}
            </div>
          </div>
          <div className="lg:w-[380px] rounded-xl border border-gray-200 dark:border-gray-700 px-4">
            <Switch checked={incluiNovas} onChange={(v) => salvar('new', { include_new: v })}
              label={t('trafficManager.scope.includeNew', 'Campanhas novas entram sozinhas')}
              help={modo === 'all'
                ? t('trafficManager.scope.includeNewHelpAll', 'As que você publicar daqui para frente já entram na gestão. Desligado: ficam de fora até você ligar aqui.')
                : t('trafficManager.scope.includeNewHelpSel', 'As que você publicar daqui para frente entram na lista. Desligado: só as marcadas.')} />
            <div className="flex gap-2 py-2.5 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500">
              <Bot className="w-4 h-4 shrink-0 text-primary-600" />
              <span>{t('trafficManager.scope.robotCreated', 'As campanhas que o robô cria (como o remarketing) entram sempre. Para tirar uma delas, desligue-a na lista.')}</span>
            </div>
          </div>
        </div>
      </Card>

      <Card padding={false}>
        <div className="p-4 sm:p-6 pb-3 flex flex-wrap items-center gap-3">
          <Megaphone className="w-5 h-5 text-primary-600" />
          <div className="inline-flex max-w-full overflow-x-auto tm-tabs rounded-lg border border-gray-200 dark:border-gray-700 p-0.5" role="group">
            {([
              ['managed', t('trafficManager.scope.fManaged', 'Sob gestão'), geridas.length],
              ['out', t('trafficManager.scope.fOut', 'Fora da gestão'), fora.length],
              ['all', t('trafficManager.scope.fAll', 'Todas'), eligible.length]
            ] as [Filtro, string, number][]).map(([k, l, n]) => (
              <button key={k} type="button" onClick={() => setFiltro(k)} aria-pressed={filtro === k}
                className={`shrink-0 whitespace-nowrap px-2.5 py-1 text-xs rounded-md ${filtro === k ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300'}`}>{l} ({n})</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[10rem] max-w-xs">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder={t('trafficManager.scope.search', 'Buscar campanha') as string} aria-label={t('trafficManager.scope.search', 'Buscar campanha') as string}
              className="w-full pl-8 pr-3 py-1.5 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div className="ml-auto inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5" role="group">
            {([
              ['ultimos_7_dias', t('trafficManager.campaigns.d7', '7 dias')],
              ['ultimos_30_dias', t('trafficManager.campaigns.d30', '30 dias')],
              ['no_mes', t('trafficManager.campaigns.month', 'Mês')]
            ] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriodo(k)} aria-pressed={periodo === k}
                className={`px-2.5 py-1 text-xs rounded-md ${periodo === k ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-y border-gray-100 dark:border-gray-700">
                <th className="pl-4 sm:pl-6 pr-2 py-2 font-medium w-16">{t('trafficManager.scope.manage', 'Gerir')}</th>
                <th className="px-3 py-2 font-medium">{t('trafficManager.campaigns.campaign', 'Campanha')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('trafficManager.campaigns.budget', 'Orçamento')}</th>
                <th className="px-3 py-2 font-medium hidden lg:table-cell">{t('trafficManager.campaigns.trend', 'Gasto 14d')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('trafficManager.metrics.spend', 'Gasto')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('trafficManager.metrics.conversions', 'Conversões')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('trafficManager.metrics.cpa', 'Custo/conv.')}</th>
                <th className="px-3 py-2 font-medium text-right hidden md:table-cell">{t('trafficManager.metrics.cpc', 'CPC')}</th>
                <th className="px-3 py-2 font-medium hidden xl:table-cell">{t('trafficManager.campaigns.lastChange', 'Última mudança do robô')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {visiveis.map((c) => {
                const m = numeros(c);
                const s = snap[c.id];
                const um = ultimaMudanca(c.id);
                const vivo = rodando(c);
                const parado = s && s.rodando_na_plataforma === false && s.status === 'active';
                return (
                  <tr key={c.id} className={`hover:bg-gray-50/60 dark:hover:bg-gray-800/40 ${c.managed ? '' : 'opacity-70'}`}>
                    <td className="pl-4 sm:pl-6 pr-2 py-2.5">
                      {salvando === c.id
                        ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        : (
                          <button type="button" role="switch" aria-checked={!!c.managed} disabled={!!salvando} onClick={() => alternar(c)}
                            aria-label={`${t('trafficManager.scope.manage', 'Gerir')}: ${c.name}`}
                            className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${c.managed ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${c.managed ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        )}
                    </td>
                    <td className="px-3 py-2.5 min-w-[14rem]">
                      <Link to={`/campaigns/${c.id}`} className="font-medium text-gray-900 dark:text-white hover:text-primary-600 line-clamp-1">{c.name}</Link>
                      <div className="text-xs text-gray-500 flex flex-wrap items-center gap-1.5">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${vivo ? 'bg-emerald-500' : parado ? 'bg-amber-500' : 'bg-gray-400'}`} />
                        <span className={parado ? 'text-amber-700 dark:text-amber-400' : ''}>
                          {parado ? (s!.veiculacao_na_plataforma === 'ENDED' ? t('trafficManager.campaigns.endedThere', 'encerrada na plataforma') : t('trafficManager.campaigns.pausedThere', 'pausada na plataforma'))
                            : vivo ? t('trafficManager.campaigns.running', 'rodando') : t('trafficManager.campaigns.paused', 'pausada')}
                        </span>
                        · {c.platforms.map(L.platform).join(' + ')}
                        {c.created_by_manager && <span className="inline-flex items-center gap-0.5 text-primary-700 dark:text-primary-300"><Bot className="w-3 h-3" />{t('trafficManager.scope.byRobot', 'criada pelo robô')}</span>}
                        {!c.managed && <span className="text-gray-500">· {c.excluded ? t('trafficManager.scope.excludedTag', 'excluída') : t('trafficManager.scope.outTag', 'fora da gestão')}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums">{f.money(s ? (s.orcamento_real_na_plataforma ?? s.orcamento) : c.budget_amount, c.currency || cur)}<span className="text-xs text-gray-500">{c.budget_type === 'daily' ? t('trafficManager.campaigns.perDay', '/dia') : ` ${t('trafficManager.campaigns.total', 'total')}`}</span></td>
                    <td className="px-3 py-2.5 hidden lg:table-cell"><Sparkline data={evolution?.series_por_campanha?.[c.id] || []} /></td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums" title={m.aprox ? (t('trafficManager.scope.only30', 'Fora da gestão: números dos últimos 30 dias') as string) : undefined}>{f.money(m.spend, c.currency || cur)}{m.aprox && <span className="text-[10px] text-gray-400 ml-0.5">30d</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{f.int(m.conversions)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums">{m.cpa != null ? f.money(m.cpa, c.currency || cur) : '—'}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums hidden md:table-cell">{m.cpc != null ? f.money(m.cpc, c.currency || cur) : '—'}</td>
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      {um
                        ? <button type="button" onClick={() => onSeeChanges(c.id)} className="text-left text-xs text-gray-600 dark:text-gray-300 hover:text-primary-600">{L.action(um.tool)} · {f.relative(um.at)}</button>
                        : <span className="text-xs text-gray-400">{t('trafficManager.campaigns.none', 'nenhuma')}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!visiveis.length && <p className="px-6 py-6 text-sm text-gray-500">{t('trafficManager.scope.emptyFilter', 'Nenhuma campanha neste filtro.')}</p>}
        </div>
        {paradas > 0 && !q && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700">
            <button type="button" onClick={() => setVerParadas((v) => !v)} className="text-sm text-primary-600 hover:underline">
              {verParadas ? t('trafficManager.campaigns.hideIdle', 'Esconder as campanhas paradas') : t('trafficManager.campaigns.showIdle', 'Mostrar {{n}} campanha(s) parada(s), sem gasto no período', { n: paradas })}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CampaignsTab;
